import { useQuery } from '@tanstack/react-query'

import { apiClient } from '../../apiClient'

const RECENT_ACTIVITY_KEY = 'mission-specialist-recent-activity'

type TrendState = 'increased' | 'decreased' | 'equal'

type ProgramData = {
  id: string
  name: string
  icon?: string
  color?: string
}

type ActivityProgramInstance = {
  programInstanceId: string
  displayName: string | null
  icon: string | null
  color: string | null
}

type ActivitySourceProgram = {
  id: string
  name: string
  icon: string | null
  color: string | null
}

type ProgramInstanceWorkActivity = {
  id: string
  programInstance: ActivityProgramInstance | null
  sourceProgram: ActivitySourceProgram | null
  tasks: {
    live: number
    recycler: number
    superMajority: number
  }
  audit: {
    correct: number
    totalAudited: number
    correctPercentage: number | null
  }
}

type TrainingStats = ProgramData & {
  passed: number
  failed: number
  programInstance: ActivityProgramInstance | null
}

type LateShiftAlert = {
  type: 'late-shift'
  timeMissed: number
  last30Days: number
}

type UnscheduledWorkAlert = {
  type: 'unscheduled-work'
  timeInWorkSession: number
  timeBusy: number
  specialistsOnline: number
}

type MissedShiftAlert = {
  type: 'missed-shift'
}

type LeftShiftEarlyAlert = {
  type: 'late-early-shift'
  timeLeftUntilEndShift: number
  engagementsMissed: number
}

type SessionSuspendedDueDropAlert = {
  type: 'session-suspended-due-drop'
}

type ShiftCancelledAlert = {
  type: 'cancelled-shift'
  last30Days: number
}

type Alert = {
  date: string
} & (
  | UnscheduledWorkAlert
  | LateShiftAlert
  | MissedShiftAlert
  | LeftShiftEarlyAlert
  | SessionSuspendedDueDropAlert
  | ShiftCancelledAlert
)

type MsActivity = {
  date: string
  tasksCounter: number
  trainingModulesCounter: number
  taskTrend: number
  taskTrendStatus: TrendState
  timeScheduled: number
  timeInWorkSession: number
  timeBusy: number
  missedTargetTimeTasksPercentage: number
  streak: number
  streakStatus: TrendState
  avgHourlyTasks: number
  avgHourlyTasksStatus: TrendState
  alerts: Alert[]
  tasks: {
    total: number
    missedTtr: number
    programInstances: ProgramInstanceWorkActivity[]
  }
  training: TrainingStats[]
}

type RecentActivityResponse = {
  activity: MsActivity[]
}

export function useGetById(msId?: string) {
  return useQuery({
    queryKey: [RECENT_ACTIVITY_KEY, msId],
    queryFn: async () => {
      const { data } = await apiClient.get<RecentActivityResponse>(
        `/int/v3/operators/${msId}/recent-activity`
      )
      return data.activity
    },
    enabled: Boolean(msId),
    refetchOnWindowFocus: false,
  })
}
